import { useSearchParams } from "solid-app-router";
import { Component, createMemo, createSignal, onCleanup } from "solid-js";
import {
  GAME_COLS,
  GAME_ROWS,
  IS_IN_WEB_APP_IOS,
  NUM_GAMES_X,
  NUM_GAMES_Y,
} from "./constants";
import GameCompleteBanner from "./GameCompleteBanner";
import { useGamesDataContext } from "./GameDataProvider";
import GameShare from "./GameShare";
import GameSquare from "./GameTile";
import Header from "./Header";
import Keyboard from "./Keyboard";
import { createResizeObserverPoly } from "./ResizeObserverPoly";
import Statistics from "./Statistics";
import Tutorial from "./Tutorial";
import Parameters from "./Parameters";
import { GameMode } from "./types";
import { gtagWrap, vibrate, getDateSeed } from "./utils";
import { PrevIcon, NextIcon, StartIcon, EndIcon } from "./icons";

/**
 * Create resize observer is a helper primitive for binding resize events.
 *
 * @param opts.refs - Either an `Element`, an array of `Element`s, or a signal returning one of these.
 * @param opts.onResize - Function handler to trigger on resize
 * @return A callback that can be used to add refs to observe resizing
 *
 */

const NUM_GAMES_X_ARR = [...Array(NUM_GAMES_X).keys()];
const NUM_GAMES_Y_ARR = [...Array(NUM_GAMES_Y).keys()];
const GAME_ROWS_ARR = [...Array(GAME_ROWS).keys()];
const GAME_COLS_ARR = [...Array(GAME_COLS).keys()];

type GameQuadrantProps = {
  mode: GameMode;
  gameX: number;
  gameY: number;
};
const GameQuadrant: Component<GameQuadrantProps> = (props) => {
  return (
    <div class="flex flex-col flex-auto p-1">
      {GAME_ROWS_ARR.map((rowIndex) => (
        <div class="flex w-full">
          {GAME_COLS_ARR.map((colIndex) => (
            <GameSquare
              mode={props.mode}
              gameX={props.gameX}
              gameY={props.gameY}
              gameRow={rowIndex}
              gameCol={colIndex}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

type GameProps = {
  mode: GameMode;
};
const Game: Component<GameProps> = (props) => {
  const [gamesData, gamesDataFuncs] = useGamesDataContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [fontSize, setFontSize] = createSignal(35);

  const keyEventListener = (e: KeyboardEvent) => {
    e.preventDefault();
    gamesDataFuncs.sendKey(props.mode, e);
  };
  document.addEventListener("keydown", keyEventListener);
  onCleanup(() => document.removeEventListener("keydown", keyEventListener));

  const tutorialOpen = createMemo(
    () => searchParams.overlay === "tutorial"
  );
  const statisticsOpen = createMemo(
    () => searchParams.overlay === "statistics"
  );
  const parametersOpen = createMemo(
    () => searchParams.overlay === "parameters"
  );
  
  const refCallback = createResizeObserverPoly({
    onResize: ({ width, height }) => {
      if ((width)&&(height)) {
        gamesDataFuncs.setDimensions(width, height);
        setFontSize(width / 15);
      }
    },
  });

  return (
    <div
      class="w-full absolute flex flex-col overflow-hidden"
      classList={{
        "h-full": !IS_IN_WEB_APP_IOS,
        "h-[calc(100%-25px)] bottom-[25px]": IS_IN_WEB_APP_IOS,
      }}
    >
      <Header
        mode={props.mode}
        onOpenTutorial={() => {
          vibrate(gamesData.vibrateMode);
          gtagWrap("event", "tutorial", {
            mode: props.mode,
          });
          setSearchParams({ overlay: "tutorial" });
        }}
        onOpenStatistics={() => {
          vibrate(gamesData.vibrateMode);
          gtagWrap("event", "statistics", {
            mode: props.mode,
          });
          setSearchParams({ overlay: "statistics" });
        }}
        onOpenParameters={() => {
          vibrate(gamesData.vibrateMode);
          gtagWrap("event", "parameters", {
            mode: props.mode,
          });
          setSearchParams({ overlay: "parameters" });
        }}
      />
      <div
        class="max-w-[550px] m-auto w-full"
        style={{
          "font-size": fontSize() + "px",
        }}
      >
        {gamesDataFuncs.isGameComplete(props.mode) && (
          <GameCompleteBanner mode={props.mode} />
        )}
      </div>
      <div
        id="quordle-game"
        class="quordle-desktop-scrollbar max-w-[550px] m-auto w-full flex-auto"
        classList={{
          "overflow-hidden": tutorialOpen() || statisticsOpen() || parametersOpen(),
          "overflow-auto": !(tutorialOpen() || statisticsOpen() || parametersOpen()),
        }}
        style={{
          "font-size": fontSize() + "px",
        }}
        ref={refCallback}
      >
        {props.mode == "archives" && (
          <>
         <div class="w-full flex-auto text-xl text-center text-gray-700 dark:text-gray-300 mt-2" style="height:34.5px;overflow:hidden;"><button class='bg-gray-700 dark:bg-gray-300 p-1 rounded-full text-white hover:text-gray-200 dark:text-gray-800 dark:hover:text-gray-900 transition-colors mr-1' title="Début" onClick={() => {vibrate(gamesData.vibrateMode); gamesDataFuncs.goToArchives(1);}}><StartIcon /></button> <button class='bg-gray-700 dark:bg-gray-300 p-1 rounded-full text-white hover:text-gray-200 dark:text-gray-800 dark:hover:text-gray-900 transition-colors mr-3' title="Précédent" onClick={() => {vibrate(gamesData.vibrateMode); gamesDataFuncs.goToArchives(gamesData.archives.seed-1);}}><PrevIcon /></button><span style="vertical-align: top;line-height: 1.5em;">{getDateSeed(gamesData.archives.seed)}</span><button class='bg-gray-700 dark:bg-gray-300 p-1 rounded-full text-white hover:text-gray-200 dark:text-gray-800 dark:hover:text-gray-900 transition-colors ml-3' title="Suivant" onClick={() => {vibrate(gamesData.vibrateMode); gamesDataFuncs.goToArchives(gamesData.archives.seed+1);}}><NextIcon /></button> <button class='bg-gray-700 dark:bg-gray-300 p-1 rounded-full text-white hover:text-gray-200 dark:text-gray-800 dark:hover:text-gray-900 transition-colors ml-1' title="Fin" onClick={() => {vibrate(gamesData.vibrateMode); gamesDataFuncs.goToArchives(1000000000);}}><EndIcon /></button></div>
          </>
        )}
        <div class="w-full flex-col">
          {NUM_GAMES_Y_ARR.map((gameY) => (
            <div class="flex w-full">
              {NUM_GAMES_X_ARR.map((gameX) => (
                <GameQuadrant mode={props.mode} gameX={gameX} gameY={gameY} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div
        class="max-w-[550px] m-auto w-full"
        style={{
          "font-size": fontSize() + "px",
        }}
      >
        {gamesDataFuncs.isGameComplete(props.mode) ? (
          <GameShare mode={props.mode} />
        ) : (
          <Keyboard mode={props.mode} />
        )}
      </div>
      <div
        class="absolute w-full h-full text-black dark:text-white bg-white dark:bg-gray-800 overflow-auto transition-all ease-in-out duration-500"
        classList={{
          "opacity-100 top-0": statisticsOpen(),
          "opacity-0 top-[100%]": !statisticsOpen(),
        }}
        style={{
          "font-size": fontSize() + "px",
        }}
      >
        <Statistics
          mode={props.mode}
          onCloseStatistics={() => {
            vibrate(gamesData.vibrateMode);
            setSearchParams({ overlay: undefined });
          }}
        />
      </div>
      <div
        class="absolute w-full h-full text-black dark:text-white bg-white dark:bg-gray-800 overflow-auto transition-all ease-in-out duration-500"
        classList={{
          "opacity-100 top-0": tutorialOpen(),
          "opacity-0 top-[100%]": !tutorialOpen(),
        }}
        style={{
          "font-size": fontSize() + "px",
        }}
      >
        <Tutorial
          mode={props.mode}
          onCloseTutorial={() => {
            vibrate(gamesData.vibrateMode);
            setSearchParams({ overlay: undefined });
          }}
        />
      </div>
      <div
      class="absolute w-full h-full text-black dark:text-white bg-white dark:bg-gray-800 overflow-auto transition-all ease-in-out duration-500"
      classList={{
        "opacity-100 top-0": parametersOpen(),
        "opacity-0 top-[100%]": !parametersOpen(),
      }}
      style={{
        "font-size": fontSize() + "px",
      }}
    >
      <Parameters
        mode={props.mode}
        onCloseParameters={() => {
          vibrate(gamesData.vibrateMode);
          setSearchParams({ overlay: undefined });
        }}
      />
    </div>
    </div>
  );
};

export default Game;
